/**
 * AccountVouchersModal - Shows all vouchers that have transactions on a specific account
 * 
 * Users can drill down from income statement or balance sheet to see the vouchers
 * that make up an account's balance. Clicking a voucher expands to show all transactions.
 */

import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Receipt, ArrowUpRight, ArrowDownRight, Calendar, FileText, AlertTriangle } from 'lucide-react';
import { 
  formatAmountWithDisplayFormat, 
  determineOptimalUnit,
  type DisplayFormat,
} from '@/components/sie/utils';

interface SIETransaction {
  account: string;
  amount: number;
  text?: string;
  quantity?: number;
  object?: string[];
}

interface SIEVoucher {
  series: string;
  number: number;
  date: string;
  text: string;
  regDate?: string;
  sign?: string;
  transactions: SIETransaction[];
}

interface AccountVouchersModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountNumber: string;
  accountName: string;
  vouchers: SIEVoucher[];
  displayFormat?: DisplayFormat;
  accountNames?: Record<string, string>;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  // Handle YYYYMMDD format
  if (dateStr.length === 8 && !dateStr.includes('-')) {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${year}-${month}-${day}`;
  }
  return dateStr;
}

export function AccountVouchersModal({
  isOpen,
  onClose,
  accountNumber,
  accountName,
  vouchers,
  displayFormat = 'auto',
  accountNames = {},
}: AccountVouchersModalProps) {
  // Filter vouchers that have transactions on this account
  const relevantVouchers = vouchers.filter(v => 
    v.transactions.some(t => t.account === accountNumber)
  );

  // Sort by date and number
  const sortedVouchers = [...relevantVouchers].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.number - b.number;
  });

  // Calculate optimal display unit for consistent formatting
  const autoUnit = useMemo(() => {
    const allAmounts = sortedVouchers.flatMap(v => 
      v.transactions.map(t => Math.abs(t.amount))
    );
    return determineOptimalUnit(allAmounts);
  }, [sortedVouchers]);

  // Helper function for formatting
  const fmt = (amount: number) => formatAmountWithDisplayFormat(amount, displayFormat, autoUnit);

  // Calculate totals for this account
  const accountTotals = sortedVouchers.reduce(
    (acc, voucher) => {
      voucher.transactions.forEach(t => {
        if (t.account === accountNumber) {
          if (t.amount > 0) {
            acc.debit += t.amount;
          } else {
            acc.credit += Math.abs(t.amount);
          }
          acc.net += t.amount;
        }
      });
      return acc;
    },
    { debit: 0, credit: 0, net: 0 }
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Verifikationer för konto {accountNumber}
          </DialogTitle>
          <DialogDescription>
            {accountName}
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="flex flex-wrap gap-4 py-3 border-y">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-normal">
              {sortedVouchers.length} verifikationer
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpRight className="h-4 w-4 text-green-600" />
            <span className="text-sm">Debet: <span className="font-medium text-green-600">{fmt(accountTotals.debit)}</span></span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowDownRight className="h-4 w-4 text-red-600" />
            <span className="text-sm">Kredit: <span className="font-medium text-red-600">{fmt(accountTotals.credit)}</span></span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Netto: <span className={`font-medium ${accountTotals.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(accountTotals.net)}</span></span>
          </div>
        </div>

        {/* Voucher list */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {sortedVouchers.length === 0 ? (
            <div className="py-8 text-center space-y-4">
              <Alert variant="destructive" className="text-left border-destructive/50 bg-destructive/10">
                <AlertTriangle className="h-5 w-5" />
                <AlertTitle>Inga verifikationer tillgängliga</AlertTitle>
                <AlertDescription>
                  <p className="mb-2">
                    SIE-filen innehåller endast periodsaldon eller årssaldon – 
                    inte individuella transaktioner.
                  </p>
                  <p className="text-sm">
                    Begär en ny SIE4-export med verifikationer från kunden för att 
                    kunna granska enskilda transaktioner.
                  </p>
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {sortedVouchers.map((voucher, idx) => {
                // Find the transaction on this account
                const accountTransaction = voucher.transactions.find(t => t.account === accountNumber);
                const accountAmount = accountTransaction?.amount || 0;

                return (
                  <AccordionItem 
                    key={`${voucher.series}${voucher.number}-${idx}`} 
                    value={`${voucher.series}${voucher.number}-${idx}`}
                    className="border rounded-lg px-4 data-[state=open]:bg-muted/30"
                  >
                    <AccordionTrigger className="hover:no-underline py-3">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className="font-mono">
                            {voucher.series}{voucher.number}
                          </Badge>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(voucher.date)}
                          </div>
                          <span className="text-sm max-w-[300px] truncate">
                            {voucher.text || '-'}
                          </span>
                        </div>
                        <div className={`font-medium tabular-nums ${accountAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {accountAmount >= 0 ? '+' : ''}{fmt(accountAmount)}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-2 pb-4 space-y-3">
                        {/* Voucher metadata */}
                        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                          {voucher.regDate && (
                            <span>Registrerad: {formatDate(voucher.regDate)}</span>
                          )}
                          {voucher.sign && (
                            <span>Signatur: {voucher.sign}</span>
                          )}
                        </div>

                        <Separator />

                        {/* All transactions in this voucher */}
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            Alla transaktioner i verifikationen
                          </div>
                          {voucher.transactions.map((trans, tIdx) => (
                            <div 
                              key={tIdx} 
                              className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
                                trans.account === accountNumber 
                                  ? 'bg-primary/10 border border-primary/20' 
                                  : 'hover:bg-muted/50'
                              }`}
                            >
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="font-mono text-xs shrink-0">{trans.account}</span>
                                {accountNames[trans.account] && (
                                  <span className="text-muted-foreground text-xs truncate">
                                    {accountNames[trans.account]}
                                  </span>
                                )}
                                {trans.text && !accountNames[trans.account] && (
                                  <span className="text-muted-foreground text-xs truncate max-w-[200px]">
                                    {trans.text}
                                  </span>
                                )}
                                {trans.object && trans.object.length > 0 && (
                                  <span className="text-xs text-muted-foreground">
                                    [{trans.object.join(', ')}]
                                  </span>
                                )}
                              </div>
                              <span className={`font-medium tabular-nums ${trans.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {fmt(trans.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
